import { signal } from '@preact/signals';
import { prefs } from './prefs';

/**
 * What kind of device is playing. Touch controls follow the pointer type by default and can be
 * forced either way from the settings, so a phone gets a joystick and a desktop keeps the keyboard.
 */
export const viewport = signal({ w: window.innerWidth, h: window.innerHeight, portrait: window.innerHeight >= window.innerWidth });

/** true when the primary pointer cannot hover: phones and tablets */
export const coarsePointer = signal(detectCoarse());

function detectCoarse(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  } catch {
    return 'ontouchstart' in window;
  }
}

export function isTouchUi(): boolean {
  const mode = prefs.value.touch ?? 'auto';
  if (mode === 'on') return true;
  if (mode === 'off') return false;
  return coarsePointer.value;
}

/** Phone-sized layout: one column, bottom navigation, bigger tap targets. */
export function isPhone(): boolean {
  const v = viewport.value;
  return Math.min(v.w, v.h) <= 560 || (isTouchUi() && v.w <= 900);
}

export function installDeviceWatch(): void {
  const update = () => {
    viewport.value = { w: window.innerWidth, h: window.innerHeight, portrait: window.innerHeight >= window.innerWidth };
    coarsePointer.value = detectCoarse();
    document.documentElement.classList.toggle('touch', isTouchUi());
    document.documentElement.classList.toggle('phone', isPhone());
  };
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', () => setTimeout(update, 120));
  // a mouse plugged into a tablet flips the pointer type
  try {
    window.matchMedia('(pointer: coarse)').addEventListener('change', update);
  } catch {
    /* older browsers */
  }
  update();
}

/** Keeps the html classes in step after the settings change the mode. */
export function refreshDeviceClasses(): void {
  document.documentElement.classList.toggle('touch', isTouchUi());
  document.documentElement.classList.toggle('phone', isPhone());
}

export const fullscreenOn = signal(!!document.fullscreenElement);

export async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
  } catch {
    /* Safari on iPhone refuses; nothing to do */
  }
  fullscreenOn.value = !!document.fullscreenElement;
}

document.addEventListener('fullscreenchange', () => {
  fullscreenOn.value = !!document.fullscreenElement;
});

/** A phone dims and locks while a thumb rests: hold a wake lock for as long as a realm is open. */
interface Sentinel {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', fn: () => void): void;
}
interface WakeLockNav {
  wakeLock?: { request(kind: 'screen'): Promise<Sentinel> };
}

let lock: Sentinel | null = null;
let wanted = false;

async function acquire(): Promise<void> {
  const api = (navigator as unknown as WakeLockNav).wakeLock;
  if (!api || lock || document.visibilityState !== 'visible') return;
  try {
    lock = await api.request('screen');
    lock.addEventListener('release', () => {
      lock = null;
    });
  } catch {
    lock = null; // a denied lock is not worth a word to the player
  }
}

export function keepAwake(on: boolean): void {
  wanted = on;
  if (on) void acquire();
  else if (lock) {
    void lock.release();
    lock = null;
  }
}

// a tab that comes back to the front has lost its lock: ask again
document.addEventListener('visibilitychange', () => {
  if (wanted && document.visibilityState === 'visible') void acquire();
});
